import type { GridCard } from '@Components/BigBrowse/Model/Classes/GridCard';
import { getCard, getGap } from '@Components/BigBrowse/Model/Classes/GridCard';
import type {
  PlaneSize,
  Position2D,
} from '@Components/BigBrowse/Model/ModelTypes';
import { PlaneTypes } from '@Components/BigBrowse/Model/ModelTypes';
import type { ViewData } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/multisection.data';
import { addSectionBg } from '@Components/BigBrowse/Views/DetailViews/MultiSectionView/common/sections.common';
import { getImageSize } from '@Utils/get-image-size';
import { Dimensions } from 'react-native';

import type {
  HeroAndTextView,
  HeroAndTextViewStyles,
} from '../../common/MultiSectionView.types';
import { getColumnWidth } from '../../merchandiser/helpers/MerchandiserCommon';
import { getMediaType, hasImagePart, hasTextPart } from './HeroAndTextLayout';
import { getTextPart } from './HeroAndTextLayoutLandscape.textPart';

export function insertPlaceholderCards(
  styles: HeroAndTextViewStyles,
  viewData: ViewData,
  section: HeroAndTextView
) {
  if (styles.imagePart) {
    viewData.layoutTable?.forEach((column, index) => {
      column.push(
        index === 0
          ? getPlaceholderCard({
              section,
              height: styles.imagePart!.size.height,
              type: PlaneTypes.Layout_HeroAndText_ImagePart,
              styles,
            })
          : getGap({
              width: getColumnWidth(),
              height: styles.imagePart!.size.height,
            })
      );
    });
  }

  if (styles.textPart) {
    addSectionBg(viewData, section.dtoData);
    viewData.layoutTable?.forEach((column, index) => {
      column.push(
        index === 0
          ? getPlaceholderCard({
              section,
              height: styles.textPart!.page.size.height,
              type: PlaneTypes.Layout_HeroAndText_TextPart,
              styles,
            })
          : getGap({
              width: getColumnWidth(),
              height: styles.textPart!.page.size.height,
            })
      );
    });
  }
}

export const layoutHeroAndTextLandscape = async ({
  section,
  viewData,
}: {
  section: HeroAndTextView;
  viewData: ViewData;
}) => {
  const styles = await getBoxLayoutLandscapeHeroText(section);
  insertPlaceholderCards(styles, viewData, section);
};

const getPlaceholderCard = ({
  section,
  height,
  type,
  styles,
}: {
  section: HeroAndTextView;
  height: number;
  type:
    | PlaneTypes.Layout_HeroAndText_ImagePart
    | PlaneTypes.Layout_HeroAndText_TextPart;
  styles: HeroAndTextViewStyles;
}): GridCard<PlaneTypes.Layout_Merchandiser> => {
  const card = getCard<PlaneTypes.Layout_Merchandiser>({
    data: {
      ...section,
      styles,
      key: `${section.key}_${type}`,
      sectionId: section.dtoData.id,
    },
    type,
    size: { width: getColumnWidth(), height },
    blockHover: true,
  });

  card.key = card.data?.key;

  return card;
};

export const getBoxLayoutLandscapeHeroText = async (
  section: HeroAndTextView
): Promise<HeroAndTextViewStyles> => {
  const { imagePartSize, imageSize } = await getImagePartSize(section);

  const imagePart = hasImagePart(section)
    ? {
        size: imagePartSize,
        imageSize,
        mediaType: getMediaType(),
        radius: 50,
        imagePosition: getImagePosition(imagePartSize, imageSize),
      }
    : undefined;

  const textPart = hasTextPart(section)
    ? await getTextPart(section)
    : undefined;

  return {
    layout: 'landscape',
    imagePart,
    textPart,
  };
};

type ImagePartSizes = {
  imagePartSize: PlaneSize;
  imageSize: PlaneSize;
};

const getImagePartSize = async (
  section: HeroAndTextView
): Promise<ImagePartSizes> => {
  const windowWidth = Dimensions.get('window').width;
  const bmpSize = await getImageSize(section.dtoData.heroImageUrl);

  const aspectRatio = bmpSize.width / bmpSize.height;
  const imageSize: PlaneSize = {
    width: windowWidth,
    height: windowWidth / aspectRatio,
  };

  return {
    imagePartSize: imageSize,
    imageSize,
  };
};

const getImagePosition = (box: PlaneSize, imageSize: PlaneSize): Position2D => {
  const y = box.height / 2 - imageSize.height / 2;

  return { x: 0, y };
};
